import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Typography } from 'src/typography';
import { DposLoyaltyDefault as DposLoyaltyIcon } from 'src/assets/dpos-loyalty-default';
import { PartyOutline } from 'src/assets/party-outline';
import { GiftOutline } from 'src/assets/gift-outline';
import { TagOutline } from 'src/assets/tag-outline';
import { FullPageLoader } from 'shared/components/loading';
import { useInstoreEnrollment } from 'src/hooks/use-instore-enrollment';
import { InstoreEnrollmentStatus } from 'src/state/instore-enrollment';
import { useLoyaltyModal } from 'src/components/modals/loyalty';
import { Statuses } from 'src/components/loyalty/use-loyalty';
import { useLoyalty } from './use-loyalty';
import { StepHeader, StepPanel, StepFooter, CenteredColumn, SkipButton, StepButton } from '../shared-components';
import { useInstoreEnrollmentAnalytics } from '../hooks/use-instore-enrollment-analytics';

export function Loyalty(): JSX.Element {
  const { dispensaryName, loading } = useLoyalty();
  const instoreEnrollment = useInstoreEnrollment();
  const { open: showLoyaltyModal } = useLoyaltyModal();
  const { trackStepViewed, trackLoyaltyCompleted, trackLoyaltySkipped } = useInstoreEnrollmentAnalytics();

  // Track step viewed on mount
  useEffect(() => {
    trackStepViewed('loyalty');
  }, [trackStepViewed]);

  const handleJoinLoyalty = async (): Promise<void> => {
    const result = await showLoyaltyModal({ surface: 'instoreEnrollment' });

    if (result?.status === Statuses.verificationRequired) {
      return;
    }

    if (result?.status === Statuses.success) {
      trackLoyaltyCompleted();
    } else {
      trackLoyaltySkipped();
      instoreEnrollment.setDeclinedLoyalty(true);
    }

    instoreEnrollment.setCompletedLoyaltyStep(true);
    instoreEnrollment.setCurrentStatus(InstoreEnrollmentStatus.connectBankAccount);
  };

  const handleSkip = (): void => {
    trackLoyaltySkipped();
    instoreEnrollment.setDeclinedLoyalty(true);
    instoreEnrollment.setCompletedLoyaltyStep(true);
    instoreEnrollment.setCurrentStatus(InstoreEnrollmentStatus.connectBankAccount);
  };

  if (loading) {
    return <FullPageLoader />;
  }

  return (
    <>
      <StepHeader title='Join loyalty program' data-testid='loyalty-header' />
      <StepPanel>
        <CenteredColumn>
          <BodyTextContainer>
            <ProgramWrapper>
              <LoyltyImage>
                <DposLoyaltyIcon />
              </LoyltyImage>
              <SubHeadingText size='small'>{dispensaryName} Loyalty</SubHeadingText>
            </ProgramWrapper>
            <BodyText size='small'>Get access to exclusive deals, discounts, events and announcements!</BodyText>
          </BodyTextContainer>

          <IconRow>
            <IconContainer>
              <TagOutline />
              <Typography.Body size='small'>Exclusive offers</Typography.Body>
            </IconContainer>
            <IconContainer>
              <GiftOutline />
              <Typography.Body size='small'>Exciting giveaways</Typography.Body>
            </IconContainer>
            <IconContainer>
              <PartyOutline />
              <Typography.Body size='small'>Special events</Typography.Body>
            </IconContainer>
          </IconRow>
        </CenteredColumn>
      </StepPanel>

      <StepFooter>
        <TermsText size='small' data-testid='terms-text'>
          By clicking “Start Earning Points” I consent to signup for the {dispensaryName} loyalty program where I will
          receive marketing communications. By doing so, I understand that I am allowing {dispensaryName} and its
          technology provider(s) to retain my personal contact details/engagement history for use in personalized
          marketing. Opt out at any time by replying "STOP" to messages. Standard message and calling rates may apply.
        </TermsText>
        <StepButton onClick={handleJoinLoyalty} loading={loading} mt='16px'>
          Join Today
        </StepButton>
        <SkipButton onClick={handleSkip}>Skip for now</SkipButton>
      </StepFooter>
    </>
  );
}

const BodyTextContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubHeadingText = styled(Typography.Body)`
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

const BodyText = styled(Typography.Body)`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 14px;
  font-weight: 400;
  text-align: center;
`;

const ProgramWrapper = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 96px;
    gap: ${spaces[4]};
    background-color: var(--dct--customized-colors-buttonsLinks);
    border-radius: var(--dct--customized-radius-lg);
    padding: ${spaces[5]};
    margin-bottom: ${spaces[6]};
    width: 100%;
    color: #fff;
  `}
`;

const TermsText = styled(Typography.Body)`
  color: #646d72;
  font-size: 12px;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spaces[6]};
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spaces[2]};

  p {
    font-size: 16px;
    line-height: 1.2;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.grey[30]};
  }
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spaces[6]};
`;

const LoyltyImage = styled.div`
  border-radius: 50%;
  color: var(--dct--customized-colors-buttonsLinks);
  display: block;
  height: 56px;
  overflow: hidden;
  width: 56px;
`;
